/**
 * consoleRecorder.js
 * In-memory ring buffer that records console logs, warnings, errors and uncaught exceptions
 * so they can be bundled and sent with user feedback reports for rapid troubleshooting.
 */

const MAX_LOGS = 150;
const MAX_MESSAGE_LENGTH = 1500;

const logBuffer = [];
let isInitialized = false;

// Safe serialization helper that avoids circular reference crashes and formats rich objects
function safeSerialize(arg, depth = 0) {
  if (depth > 3) return '[Deep Object]';
  if (arg === null) return 'null';
  if (arg === undefined) return 'undefined';

  if (typeof arg === 'string') {
    return arg.length > MAX_MESSAGE_LENGTH ? arg.substring(0, MAX_MESSAGE_LENGTH) + '... (truncated)' : arg;
  }
  if (typeof arg === 'number' || typeof arg === 'boolean') {
    return String(arg);
  }
  if (typeof arg === 'function') {
    return `[Function: ${arg.name || 'anonymous'}]`;
  }
  if (arg instanceof Error) {
    return {
      name: arg.name,
      message: arg.message,
      stack: arg.stack
    };
  }
  if (typeof Element !== 'undefined' && arg instanceof Element) {
    return `<${arg.tagName.toLowerCase()}${arg.id ? ` id="${arg.id}"` : ''}${arg.className ? ` class="${arg.className}"` : ''}>`;
  }

  if (typeof arg === 'object') {
    try {
      const seen = new WeakSet();
      return JSON.parse(
        JSON.stringify(arg, (key, value) => {
          if (typeof value === 'object' && value !== null) {
            if (seen.has(value)) return '[Circular]';
            seen.add(value);
          }
          if (typeof value === 'function') return `[Function]`;
          if (typeof value === 'string' && value.length > MAX_MESSAGE_LENGTH) {
            return value.substring(0, MAX_MESSAGE_LENGTH) + '...';
          }
          return value;
        })
      );
    } catch {
      return String(arg);
    }
  }

  return String(arg);
}

function pushLog(level, args, stack = null) {
  try {
    const now = new Date();
    const timeFormatted = now.toLocaleTimeString('de-DE', { hour12: false }) + '.' + String(now.getMilliseconds()).padStart(3, '0');
    
    const formattedMessages = Array.from(args).map(arg => safeSerialize(arg));

    const entry = {
      level, // 'log' | 'info' | 'warn' | 'error' | 'debug' | 'exception' | 'unhandledrejection'
      timestamp: now.toISOString(),
      timeFormatted,
      messages: formattedMessages,
      stack: stack || (level === 'error' && args[0] instanceof Error ? args[0].stack : undefined)
    };

    logBuffer.push(entry);
    if (logBuffer.length > MAX_LOGS) {
      logBuffer.shift();
    }
  } catch (e) {
    // Avoid any logger internal crashes
  }
}

export function initConsoleRecorder() {
  if (isInitialized || typeof window === 'undefined') return;
  isInitialized = true;

  const originalLog = console.log;
  const originalInfo = console.info;
  const originalWarn = console.warn;
  const originalError = console.error;
  const originalDebug = console.debug;

  console.log = function (...args) {
    pushLog('log', args);
    originalLog.apply(console, args);
  };

  console.info = function (...args) {
    pushLog('info', args);
    originalInfo.apply(console, args);
  };

  console.warn = function (...args) {
    pushLog('warn', args);
    originalWarn.apply(console, args);
  };

  console.error = function (...args) {
    pushLog('error', args);
    originalError.apply(console, args);
  };

  console.debug = function (...args) {
    pushLog('debug', args);
    originalDebug.apply(console, args);
  };

  // Catch global runtime errors
  window.addEventListener('error', (event) => {
    pushLog('exception', [
      event.message || 'Uncaught Error',
      {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      }
    ], event.error?.stack);
  });

  // Catch unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    pushLog('unhandledrejection', [
      reason?.message || (typeof reason === 'string' ? reason : 'Unhandled Promise Rejection'),
      reason
    ], reason?.stack);
  });
}

export function getConsoleLogs() {
  return [...logBuffer];
}

export function getConsoleSummary() {
  let errors = 0;
  let warnings = 0;
  let logs = 0;
  let exceptions = 0;

  for (const item of logBuffer) {
    if (item.level === 'error') errors++;
    else if (item.level === 'exception' || item.level === 'unhandledrejection') exceptions++;
    else if (item.level === 'warn') warnings++;
    else logs++;
  }

  return {
    total: logBuffer.length,
    errors: errors + exceptions,
    warnings,
    logs
  };
}

export function clearConsoleLogs() {
  logBuffer.length = 0;
}
